import { useEffect, useState } from "react";

import { Callout, Classes, Colors, Icon } from "@blueprintjs/core";
import { isAfter } from "date-fns";

import { pluralize } from "@/components/helpers/string-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { buildComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import {
  COMPONENT_STATUS_STYLES,
  TASK_TYPES,
} from "@/components/pages_logged_in/program/components/constants";
import QuestionStatistic from "@/components/pages_logged_in/program/components/QuestionStatistic";
import { ActionItemAnswer } from "@/components/pages_logged_in/program/components/set-components/ActionItemDisplay";
import { CARD_CLASSES, MIDTEXT_COLOR, SUBTEXT_COLOR } from "@/css/constants";
import {
  ComponentStatus,
  ComponentTypeEnum,
  FormFieldAnswerType,
  useProgramSurveyQuestionParticipantsQueryLazyQuery,
  useProgramSurveyQuestionsAnalyticsQueryLazyQuery,
} from "@/graphql";
import { MainComponent, ProgramParticipant } from "@/types/programs";
import { QuestionField, SurveyAnswer } from "@/types/surveys";
import { ensureNonNullArray } from "@/utils/array";

import { ParticipantResponseStatusTableV2 } from "./ParticipantResponseStatusTableV2";

const getResponseStatus = (
  component: MainComponent & {
    participantCompletionStatus?: ComponentStatus;
  },
  answer?: SurveyAnswer | null,
) => {
  const currentTime = new Date();
  const { targetEndDate, closedAt } = component || {};
  const endDate = targetEndDate ? new Date(targetEndDate) : undefined;

  // completed
  if (answer) return ComponentStatus.Completed;

  // not completed
  if (closedAt || (endDate && isAfter(currentTime, endDate))) {
    return ComponentStatus.NoResponse;
  }

  return ComponentStatus.NotStarted;
};

interface GetStatisticDataFormatedForItemProps {
  component: MainComponent & {
    participantCompletionStatus?: ComponentStatus;
  };
  completedCount?: number | null;
  totalParticipants?: number | null;
}

export function getStatisticDataFormatedForItem({
  component,
  completedCount,
  totalParticipants,
}: GetStatisticDataFormatedForItemProps) {
  const { status } = component || {};
  let incompleteCount = (totalParticipants ?? 0) - (completedCount ?? 0);
  if (incompleteCount < 0) {
    incompleteCount = 0;
  }

  const completeStatus = COMPONENT_STATUS_STYLES[ComponentStatus.Completed];
  const noResponseStatus = COMPONENT_STATUS_STYLES[ComponentStatus.NoResponse];
  const notStartedStatus = COMPONENT_STATUS_STYLES[ComponentStatus.NotStarted];

  const statisticData: {
    label?: string;
    color?: string;
    borderColor?: string;
    count?: number | null;
  }[] = [
    {
      label: completeStatus?.text,
      color: completeStatus?.color,
      borderColor: completeStatus?.borderColor,
      count: completedCount,
    },
  ];

  if (status === ComponentStatus.Completed && noResponseStatus) {
    statisticData.push({
      label: noResponseStatus.text,
      color: noResponseStatus.color,
      borderColor: noResponseStatus.borderColor,
      count: incompleteCount || 0,
    });
  } else if (status === ComponentStatus.Ongoing && notStartedStatus) {
    statisticData.push({
      label: notStartedStatus.text,
      color: notStartedStatus.color,
      borderColor: notStartedStatus.borderColor,
      count: incompleteCount || 0,
    });
  }

  return statisticData;
}

interface TextFormFieldAnswerProps {
  question: QuestionField;
  component: MainComponent;
  type?: ComponentTypeEnum;
  surveyId?: string;
}

function TextFormFieldAnswer({
  question,
  component,
  type = ComponentTypeEnum.Survey,
  surveyId,
}: TextFormFieldAnswerProps) {
  const [seeMoreBtnVisible, setSeeMoreBtnVisible] = useState(true);

  const {
    id,
    prompt,
    answers,
    totalResponses,
    answerType,
    link,
    totalParticipants,
  } = question;

  const [showStatusTable, setShowStatusTable] = useState(false);
  const { url, text } = link || {};

  function onSeeMore(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    setSeeMoreBtnVisible(false);
  }

  const [fetch, { data, loading }] =
    useProgramSurveyQuestionParticipantsQueryLazyQuery({
      fetchPolicy: "no-cache",
    });

  const participants = ensureNonNullArray(
    data?.programSurveyQuestionParticipants,
  );

  useEffect(() => {
    if (showStatusTable && surveyId) {
      fetch({ variables: { programSurveyId: surveyId, formFieldId: id } });
    }
  }, [showStatusTable]);

  const nonNullAnswers = ensureNonNullArray(answers);
  const isDisplaySeeMore = nonNullAnswers.length > 3 && seeMoreBtnVisible;
  const displayAnswers = isDisplaySeeMore
    ? nonNullAnswers.slice(0, 3)
    : nonNullAnswers;

  const isSurvey = type === ComponentTypeEnum.Survey;
  const isActionItems = type === ComponentTypeEnum.ActionItem;
  const taskType = TASK_TYPES.find((task) => task.value === answerType);

  const statisticData = getStatisticDataFormatedForItem({
    component,
    completedCount: totalResponses,
    totalParticipants,
  });

  return (
    <Box key={id} mt={24} className={CARD_CLASSES} p={16}>
      {isActionItems && taskType && (
        <Box fontWeight={500} color={SUBTEXT_COLOR} fontSize={16} mb={2}>
          {taskType.name}
        </Box>
      )}
      <Box fontWeight={500} fontSize={18}>
        {prompt}
      </Box>
      {isActionItems && link && (
        <Flex
          fontWeight={500}
          fontSize={14}
          color={MIDTEXT_COLOR}
          alignItems={"center"}
          mt={2}
        >
          <Icon icon="link" size={14} />
          <Box ml={1}>
            <a
              style={{ color: MIDTEXT_COLOR }}
              href={url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              {text}
            </a>
          </Box>
        </Flex>
      )}
      {isActionItems && (
        <>
          <Box
            my={16}
            flex={1}
            height={12}
            width={1}
            display={["none", "flex"]}
          >
            {statisticData
              .filter((item) => item.count && item.count > 0)
              .map((item, index) => (
                <Flex
                  key={item.label}
                  flex={item.count!}
                  justifyContent={"center"}
                  css={
                    index !== statisticData.length - 1
                      ? { marginRight: 5 }
                      : null
                  }
                >
                  <Box
                    width={1}
                    backgroundColor={item.color}
                    height={12}
                    css={{
                      border: `solid 1px ${item.borderColor}`,
                      borderRadius: 4,
                    }}
                  ></Box>
                </Flex>
              ))}
          </Box>
          <Flex flex={1} flexDirection={["column", "row"]}>
            {statisticData.map((item) => (
              <Box key={item.label} mr={"10px"} justifyContent={"center"}>
                <Box color={SUBTEXT_COLOR}>{item.label}</Box>
                <Flex alignItems={"center"}>
                  <Box
                    backgroundColor={item.color}
                    width={12}
                    height={12}
                    css={{
                      border: `solid 1px ${item.borderColor}`,
                      borderRadius: 4,
                    }}
                  ></Box>
                  <Box fontSize={18} fontWeight={600} ml={"5px"}>
                    {item.count}
                  </Box>
                </Flex>
              </Box>
            ))}
          </Flex>
          <Box my={3}>
            <a onClick={() => setShowStatusTable(!showStatusTable)}>
              See Responses Per Participant{" "}
              <Icon icon={!showStatusTable ? "chevron-down" : "chevron-up"} />
            </a>
          </Box>
          {showStatusTable &&
            (loading ? (
              <Box mt={4} className={Classes.SKELETON} height={100} />
            ) : participants.length > 0 ? (
              <Box>
                <ParticipantResponseStatusTableV2
                  participants={participants.map((p) => {
                    const answer = answers?.find((a) => a?.user?.id === p.id);
                    const participant: ProgramParticipant & {
                      participantCompletionStatus: ComponentStatus;
                    } = {
                      id: p.id || "",
                      user: {
                        __typename: "MiniUserType",
                        id: p.id || "",
                        email: p.email || "",
                        name: p.name || "",
                        photo: p.photo || "",
                      },
                      participantCompletionStatus: getResponseStatus(
                        component,
                        answer,
                      ),
                    };
                    return participant;
                  })}
                  component={component}
                />
              </Box>
            ) : (
              <Box mt={4}>
                <Callout intent="danger" icon={null}>
                  No responses found
                </Callout>
              </Box>
            ))}
        </>
      )}

      {isSurvey && (
        <Flex alignItems={"center"} my={24} color={SUBTEXT_COLOR}>
          <Box fontSize={18} fontWeight={600} mr={"5px"} color={Colors.BLACK}>
            {totalResponses}
          </Box>
          {pluralize({
            amount: totalResponses,
            singular: "response",
            plural: "responses",
            showCount: false,
          })}
        </Flex>
      )}
      {displayAnswers &&
        isSurvey &&
        displayAnswers.map((answer, index) => {
          const { user } = answer;
          return (
            <Box
              key={answer?.id}
              backgroundColor={"#FBFCFF"}
              p={16}
              my={24}
              className="round-corners"
            >
              <Box fontWeight={500} mb={1}>
                {user?.name || user?.email}
              </Box>
              <ActionItemAnswer
                key={index}
                answer={answer}
                answerType={answerType}
              />
            </Box>
          );
        })}
      {isDisplaySeeMore && isSurvey ? (
        <Box>
          <a onClick={onSeeMore} href="#" style={{ fontWeight: 600 }}>
            See more
          </a>
        </Box>
      ) : null}
    </Box>
  );
}

interface SurveyQuestionAnalyticsProps {
  surveyStatus: ComponentStatus;
  component: MainComponent;
}

export function SurveyQuestionAnalytics({
  surveyStatus,
  component,
}: SurveyQuestionAnalyticsProps) {
  const [fetch, { data, loading }] =
    useProgramSurveyQuestionsAnalyticsQueryLazyQuery();

  const surveyId = component?.survey?.id;

  useEffect(() => {
    if (surveyId) {
      fetch({ variables: { programSurveyId: surveyId } });
    }
  }, [surveyId]);

  const programSurveyQuestionsAnalytics = ensureNonNullArray(
    data?.programSurveyQuestionsAnalytics,
  );

  const metaData = buildComponentMetadata({
    component,
    isProgramLead: true,
  });

  if (loading && !data) {
    return (
      <Box
        px={[16, 24]}
        py={[16, 24]}
        flex={1}
        className={CARD_CLASSES}
        css={{ background: Colors.WHITE }}
      >
        <Box fontSize={18} fontWeight={600}>
          {metaData.analyticsTitle}
        </Box>
        <Box mt={24} className={Classes.SKELETON} height={25} />
        <Box mt={12} className={Classes.SKELETON} height={100} />
        <Flex mt={12} flexDirection={"row"}>
          <Box
            className={Classes.SKELETON}
            height={50}
            width={100}
            mr={10}
            maxWidth={"20%"}
          />
          <Box
            className={Classes.SKELETON}
            height={50}
            width={100}
            mr={10}
            maxWidth={"20%"}
          />
          <Box
            className={Classes.SKELETON}
            height={50}
            width={100}
            mr={10}
            maxWidth={"20%"}
          />
          <Box
            className={Classes.SKELETON}
            height={50}
            width={100}
            mr={10}
            maxWidth={"20%"}
          />
          <Box
            className={Classes.SKELETON}
            height={50}
            width={100}
            mr={10}
            maxWidth={"20%"}
          />
        </Flex>
        <Box mt={24} className={Classes.SKELETON} height={25} />
        <Box mt={12} className={Classes.SKELETON} height={100} />
      </Box>
    );
  }

  return (
    <Box
      px={[16, 24]}
      py={[16, 24]}
      flex={1}
      mt={24}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
    >
      <Box fontSize={18} fontWeight={600}>
        {metaData.analyticsTitle}
      </Box>
      {surveyStatus !== "Completed" && (
        <Box mt={24}>
          <Callout intent="primary" icon={null}>
            💡 This {metaData.componentTypeName} is still open. The data below
            will update as we receive new responses.
          </Callout>
        </Box>
      )}
      {programSurveyQuestionsAnalytics.length === 0 && (
        <Box mt={24}>
          <Callout intent="danger" icon={null}>
            No questions found
          </Callout>
        </Box>
      )}
      {metaData.isSurvey &&
        programSurveyQuestionsAnalytics.map((question) => {
          const { id, prompt, answerType } = question;
          if (answerType === FormFieldAnswerType.RatingScale) {
            return (
              <Box key={id} mt={24} className="bas round-corners" p={[16, 24]}>
                <Box fontWeight={500} fontSize={18}>
                  {prompt}
                </Box>
                <QuestionStatistic question={question} />
              </Box>
            );
          }
          return (
            <TextFormFieldAnswer
              question={question}
              key={id}
              component={component}
              surveyId={surveyId}
            />
          );
        })}
      {metaData.isActionItems &&
        programSurveyQuestionsAnalytics.map((question) => {
          return (
            <TextFormFieldAnswer
              question={question}
              key={question.id}
              type={ComponentTypeEnum.ActionItem}
              component={component}
              surveyId={surveyId}
            />
          );
        })}
    </Box>
  );
}
